import GroupFilterDto from "../../classes/dtos/datasourcemodule/GroupFilterDto";
import FilterParameterDto from "../../classes/dtos/datasourcemodule/FilterParameterDto";
import FilterParameterValueDto from "../../classes/dtos/datasourcemodule/FilterParameterValueDto";
import FilterParameterVariableDto from "../../classes/dtos/datasourcemodule/FilterParameterVariableDto";
import FilterConstantValue from "../../classes/dtos/datasourcemodule/FilterConstantValue";
import {
    ComparisonOperators,
    LogicalOperator,
    isValue,
    isVariable,
    nextTempId,
} from "./FilterSerialization";

// ─── Type DevExtreme Filter Expression ───────────────────────────────────────

export type FilterValue = string | number | boolean | Date | null;
export type FilterCondition = [string, string, FilterValue];
export type FilterExpression = (FilterCondition | FilterExpression | string)[];

// Opérations réellement référencées par une expression de filtre, indexées par dataField
// (FieldReferenceId string). Sert à conserver dans le `filterOperations` d'un champ une opération
// utilisée par une condition *déjà enregistrée* même si la liste de paramètres courante ne la
// proposerait pas (ex. une condition "Variable" "=v" dont le paramètre n'est plus défini) : sans ça
// le FilterBuilder lève E1048 en tentant d'afficher cette condition.
export function collectOperationsByField(
    expression: FilterExpression | FilterCondition | string | null | undefined
): Map<string, Set<string>> {
    const result = new Map<string, Set<string>>();

    const visit = (node: unknown): void => {
        if (!Array.isArray(node)) {
            return;
        }
        // Groupe nié : ["!", <filtre>]
        if (node[0] === "!") {
            visit(node[1]);
            return;
        }
        // Condition : [dataField, opération, valeur]
        if (typeof node[0] === "string") {
            if (typeof node[1] === "string") {
                const set = result.get(node[0]) ?? new Set<string>();
                set.add(node[1]);
                result.set(node[0], set);
            }
            return;
        }
        // Groupe : [ <partie>, "and"|"or", <partie>, ... ]
        node.forEach(visit);
    };

    visit(expression);
    return result;
}

// ─── Mapping des opérateurs ───────────────────────────────────────────────────

// Un paramètre (Variable) réutilise l'opérateur de comparaison avec un suffixe
// "v", qui correspond au nom d'une customOperation FilterBuilder dédiée
// (voir buildParameterOperations dans FilterFieldsShared.tsx) ayant son propre
// éditeur (sélection d'un paramètre nommé) plutôt qu'un éditeur de constante.
const PARAMETER_OPERATOR_SUFFIX = "v";

const COMPARISON_OPERATOR_MAP: Record<number, string> = {
    [ComparisonOperators.LessThan]: "<",
    [ComparisonOperators.GreaterThan]: ">",
    [ComparisonOperators.LessThanOrEqualTo]: "<=",
    [ComparisonOperators.GreaterThanOrEqualTo]: ">=",
    [ComparisonOperators.EqualTo]: "=",
    [ComparisonOperators.NotEqualTo]: "<>"
};

const LOGICAL_OPERATOR_MAP: Record<number, string> = {
    [LogicalOperator.And]: "and",
    [LogicalOperator.Or]: "or",
    [LogicalOperator.NotEnd]: "notand",
    [LogicalOperator.NotOr]: "notor"
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveComparisonOperator(op: ComparisonOperators): string {
    const mapped = COMPARISON_OPERATOR_MAP[op];
    if (!mapped) throw new Error(`Opérateur de comparaison inconnu : ${op}`);
    return mapped;
}

function resolveLogicalOperator(op: LogicalOperator): string {
    const mapped = LOGICAL_OPERATOR_MAP[op];
    if (!mapped) throw new Error(`Opérateur logique inconnu : ${op}`);
    return mapped;
}

function resolveOperator(param: FilterParameterDto): string {
    const base = resolveComparisonOperator(param.Operator);
    return isVariable(param) ? base + PARAMETER_OPERATOR_SUFFIX : base;
}

// Une condition "Variable" est éditée dans le FilterBuilder via le TechnicalName du paramètre référencé
// (SelectBox valueExpr="TechnicalName", voir FilterFieldsShared). Côté back la variable porte l'objet
// DataTableFieldParameterDefinition { Id, TechnicalName } ; le FilterBuilder ne round-trip que le
// TechnicalName, donc conditionToParameter reconstruit une définition Id 0 (le back la (re)crée / dédup
// par TechnicalName au save).
// Une condition "Constante" porte un scalaire JSON typé (FilterParameterValueDto.Value) : on le
// décode via FilterConstantValue selon le FieldType du champ avant de le rendre au FilterBuilder.
function resolveValue(
    param: FilterParameterDto,
    resolveType: (fieldReferenceId: number) => number
): FilterValue {
    if (isValue(param)) {
        return FilterConstantValue.parse(param.Value, resolveType(param.FieldReferenceId)).toFilterBuilderValue();
    }
    return (param as FilterParameterVariableDto).TechnicalName;
}

// ─── Mapping inverse (reconstruction) ─────────────────────────────────────────

const REVERSE_COMPARISON_OPERATOR_MAP: Record<string, ComparisonOperators> = {
    "<": ComparisonOperators.LessThan,
    ">": ComparisonOperators.GreaterThan,
    "<=": ComparisonOperators.LessThanOrEqualTo,
    ">=": ComparisonOperators.GreaterThanOrEqualTo,
    "=": ComparisonOperators.EqualTo,
    "<>": ComparisonOperators.NotEqualTo,
};

const REVERSE_LOGICAL_OPERATOR_MAP: Record<string, LogicalOperator> = {
    "and": LogicalOperator.And,
    "or": LogicalOperator.Or,
    "notand": LogicalOperator.NotEnd,
    "notor": LogicalOperator.NotOr,
};

function resolveComparisonOperatorReverse(op: string): ComparisonOperators {
    const base = op.endsWith(PARAMETER_OPERATOR_SUFFIX) ? op.slice(0, -PARAMETER_OPERATOR_SUFFIX.length) : op;
    const mapped = REVERSE_COMPARISON_OPERATOR_MAP[base];
    if (!mapped) throw new Error(`Opérateur de comparaison inconnu : ${op}`);
    return mapped;
}

function resolveLogicalOperatorReverse(op: string): LogicalOperator {
    const mapped = REVERSE_LOGICAL_OPERATOR_MAP[op];
    if (!mapped) throw new Error(`Opérateur logique inconnu : ${op}`);
    return mapped;
}

// Une condition est un triplet [champ, opérateur, valeur] : son 1er élément est le nom du champ (string).
// Un groupe imbriqué a toujours pour 1er élément soit une condition (tableau), soit un autre groupe (tableau) :
// jamais une string en position 0, ce qui permet de distinguer les deux sans ambiguïté.
function isConditionArray(part: FilterCondition | FilterExpression): part is FilterCondition {
    return typeof part[0] === 'string';
}

// Le type (Constante/Paramètre) d'une condition se lit directement sur son opérateur (suffixe "v" ou non) :
// pas besoin de le faire porter par ailleurs (champ, mode global...), ce qui permet de mélanger librement
// des conditions Constante et Paramètre dans un même arbre édité par un seul FilterBuilder.
function conditionToParameter(
    condition: FilterCondition,
    resolveType: (fieldReferenceId: number) => number
): FilterParameterDto | null {
    const [dataField, operatorStr, value] = condition;
    const fieldReferenceId = Number(dataField);
    const asVariable = operatorStr.endsWith(PARAMETER_OPERATOR_SUFFIX);
    const operator = resolveComparisonOperatorReverse(operatorStr);

    if (asVariable) {
        const technicalName = value as string;
        // Condition "Variable" sans paramètre résolu : l'utilisateur vient d'ajouter l'opération "=v" sans
        // encore choisir de paramètre, ou la définition liée a été supprimée. FilterBuilder remonte alors la
        // condition incomplète via onValueChanged ; on l'ignore au lieu de lever (ce qui plantait l'éditeur).
        // Elle sera prise en compte dès qu'un paramètre est sélectionné.
        if (technicalName == null || technicalName === "") return null;
        return new FilterParameterVariableDto(nextTempId(), fieldReferenceId, operator, technicalName);
    }

    // Sérialisation du scalaire JSON typé (Date -> "yyyy-MM-dd" locale, nombre, booléen…), pilotée par
    // le FieldType du champ — voir FilterConstantValue. Remplace l'ancien String(value)/toISOString().
    const serializedValue = FilterConstantValue.fromInput(value, resolveType(fieldReferenceId)).toJson();
    return new FilterParameterValueDto(nextTempId(), fieldReferenceId, operator, serializedValue);
}

// ─── Conversion récursive FilterExpression → GroupFilterDto ──────────────────

// Reconstruit l'arbre GroupFilterDto à partir de la valeur éditée par FilterBuilder (sens inverse de
// groupFilterToExpression).
export function filterExpressionToGroupFilter(
    expression: FilterExpression | null | undefined,
    resolveType: (fieldReferenceId: number) => number
): GroupFilterDto | null {

    // FilterBuilder hands back `null` once every node has been removed.
    if (expression == null || expression.length === 0) return null;

    if (isConditionArray(expression as FilterCondition)) {
        const parameter = conditionToParameter(expression as FilterCondition, resolveType);
        return parameter ? new GroupFilterDto(nextTempId(), LogicalOperator.And, [], [parameter]) : null;
    }

    const parts = expression.filter((_, i) => i % 2 === 0) as (FilterCondition | FilterExpression)[];
    const separator = expression.length > 1 ? (expression[1] as string) : "and";
    const operator = resolveLogicalOperatorReverse(separator);

    const parameters: FilterParameterDto[] = [];
    const childrens: GroupFilterDto[] = [];

    for (const part of parts) {
        if (isConditionArray(part)) {
            const parameter = conditionToParameter(part, resolveType);
            if (parameter) parameters.push(parameter);
        } else {
            const child = filterExpressionToGroupFilter(part as FilterExpression, resolveType);
            if (child) childrens.push(child);
        }
    }

    // Toutes les conditions du groupe étaient incomplètes (ex. "Variable" sans paramètre) : rien à committer.
    if (parameters.length === 0 && childrens.length === 0) return null;

    return new GroupFilterDto(nextTempId(), operator, childrens, parameters);
}

// ─── Conversion d'un paramètre → condition [champ, op, valeur] ───────────────

function paramToCondition(
    param: FilterParameterDto,
    resolveType: (fieldReferenceId: number) => number
): FilterCondition {
    return [
        String(param.FieldReferenceId),
        resolveOperator(param),
        resolveValue(param, resolveType),
    ];
}

// ─── Interleave : [a, b, c] → [a, "and", b, "and", c] ───────────────────────

function interleave(
    parts: (FilterCondition | FilterExpression)[],
    separator: string
): FilterExpression {
    if (parts.length === 0) return [];
    if (parts.length === 1) return parts[0] as FilterExpression;

    return parts.reduce<FilterExpression>((acc, part, i) => {
        if (i > 0) acc.push(separator);
        acc.push(part);
        return acc;
    }, []);
}

// ─── Conversion récursive GroupFilterDto → FilterExpression ──────────────────

export function groupFilterToExpression(
    node: GroupFilterDto,
    resolveType: (fieldReferenceId: number) => number
): FilterExpression {

    const logical = resolveLogicalOperator(node.Operator);

    const parts: (FilterCondition | FilterExpression)[] = [
        ...node.Parameters.map((p) => paramToCondition(p, resolveType)),
        ...node.Childrens.map((c) => groupFilterToExpression(c, resolveType)),
    ];

    return interleave(parts, logical);
}

// ─── GroupFilterDto → FilterExpression (lecture initiale du FilterBuilder) ───

// Lecture seule : ne sert qu'à dériver l'état initial du FilterBuilder. L'écriture (FilterExpression →
// GroupFilterDto) passe par filterExpressionToGroupFilter côté appelant — voir FilterBuilderInline.
export function deriveFilterExpression(
    filter: GroupFilterDto | null,
    resolveType: (fieldReferenceId: number) => number
): FilterExpression {
    if (!filter) return [];

    return groupFilterToExpression(filter, resolveType);
}
